import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import Sidebar from './Sidebar';
import { SyncOverlay } from '../../components/Loader';
import { useAuth } from '../../context/AuthContext';
import { vehicleService } from '../../services/api';
import { DepartementProvider, useDepartement } from '../../context/DepartementContext';
import { formatDate, formatTime } from '../../utils/formatters';
import useSessionTimeout from '../../hooks/useSessionTimeout';
import './MainLayout.css';

function MainLayoutInner() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { departements, selectedDepartement, setSelectedDepartement } = useDepartement();

  const [collapsed, setCollapsed]       = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [syncVisible, setSyncVisible]   = useState(false);
  const [syncError, setSyncError]       = useState('');
  const [dateTime, setDateTime]         = useState(new Date());

  const dropdownRef = useRef(null);

  // ── Session timeout ────────────────────────────────────────────────
  const handleConfirmLogout = useCallback(() => {
    logout();
    navigate('/login');
  }, [logout, navigate]);

  const { showWarning, remaining, extendSession } = useSessionTimeout({
    timeoutMs: 30 * 60 * 1000, // 30 minutes inactivity → auto-logout (2 mins for testing)
    warningMs:  2 * 60 * 1000, // warn 2 minute before
    onTimeout: handleConfirmLogout,
  });

  // ── Live clock ─────────────────────────────────────────────────────
  useEffect(() => {
    const interval = setInterval(() => setDateTime(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);

  // ── Close dropdown on outside click ───────────────────────────────
  useEffect(() => {
    function handle(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener('click', handle);
    return () => document.removeEventListener('click', handle);
  }, []);

  // ── Sync ──────────────────────────────────────────────────────────
  const handleSync = async () => {
    setDropdownOpen(false);
    setSyncError('');
    try {
      await vehicleService.sync();
      setSyncVisible(true);
    } catch (err) {
      const msg = err.response?.data?.message || 'Sync failed. Please try again.';
      setSyncError(msg);
    }
  };

  const handleLogoutClick = () => {
    setDropdownOpen(false);
    setShowLogoutModal(true);
  };

  const handleSyncHide = useCallback(() => setSyncVisible(false), []);

  // Format remaining time as mm:ss
  const formatRemaining = (secs) => {
    const m = Math.floor(secs / 60).toString().padStart(2, '0');
    const s = (secs % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  return (
    <>
      <SyncOverlay visible={syncVisible} onHide={handleSyncHide} />

      {/* ── Session timeout warning ── */}
      {showWarning && (
        <div className="session-warning-backdrop">
          <div className="session-warning-card">
            <div className="session-warning-icon">
              <i className="fas fa-clock"></i>
            </div>
            <h3>Session Expiring</h3>
            <p>You've been inactive. You will be logged out in:</p>
            <div className="session-countdown">{formatRemaining(remaining)}</div>
            <div className="logout-modal-actions">
              <button className="btn-logout-cancel" onClick={extendSession}>
                Stay logged in
              </button>
              <button className="btn-logout-confirm" onClick={handleConfirmLogout}>
                Log out now
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Logout confirmation modal ── */}
      {showLogoutModal && (
        <div
          className="logout-modal-backdrop"
          onClick={(e) => e.target === e.currentTarget && setShowLogoutModal(false)}
        >
          <div className="logout-modal-card">
            <div className="logout-icon">
              <i className="fas fa-sign-out-alt"></i>
            </div>
            <h3>Log out?</h3>
            <p>Are you sure you want to logout?</p>
            <div className="logout-modal-actions">
              <button
                className="btn-logout-cancel"
                onClick={() => setShowLogoutModal(false)}
              >
                Cancel
              </button>
              <button className="btn-logout-confirm" onClick={handleConfirmLogout}>
                Log out
              </button>
            </div>
          </div>
        </div>
      )}

      <Sidebar collapsed={collapsed} onToggle={() => setCollapsed((c) => !c)} />

      <div className={`main-content${collapsed ? ' expanded' : ''}`} id="mainContent">
        <div className="page-header">
          <div id="page-title-slot"></div>

          {/* ── Department filter ── */}
          <div className="dept-filter">
            <div className="dept-filter-icon-wrap">
              <i className="fas fa-sitemap"></i>
            </div>
            <div className="dept-filter-body">
              <span className="dept-filter-label">Department</span>
              <select
                className="dept-filter-select"
                value={selectedDepartement?.id || ''}
                onChange={(e) => {
                  const dept = departements.find((d) => d.id === e.target.value) || null;
                  setSelectedDepartement(dept);
                }}
              >
                <option value="">All departments</option>
                {departements.map((d) => (
                  <option key={d.id} value={d.id}>{d.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="header-user-trigger" ref={dropdownRef}>
            <div
              className="user-info"
              onClick={(e) => {
                e.stopPropagation();
                setDropdownOpen((o) => !o);
              }}
            >
              <span className="header-datetime">
                <span className="date-part">{formatDate(dateTime)}</span>{' '}
                <span className="time-part">{formatTime(dateTime)}</span>
              </span>
              <div className="user-avatar">{user?.initials || 'AD'}</div>
              <span className="header-username">{user?.name || 'Admin'}</span>
            </div>

            {dropdownOpen && (
              <div className="user-dropdown">
                {syncError && (
                  <div className="user-dropdown-error">{syncError}</div>
                )}
                <button className="user-dropdown-item" onClick={handleSync}>
                  <i className="fas fa-sync-alt"></i> Sync data
                </button>
                <button className="user-dropdown-item logout" onClick={handleLogoutClick}>
                  <i className="fas fa-sign-out-alt"></i> Logout
                </button>
              </div>
            )}
          </div>
        </div>

        <Outlet context={{ collapsed }} />

        <footer className="app-footer">
          <div>© 2026 Beachcomber. All rights reserved.</div>
          <div className="footer-made-with">
            Made with <i className="fas fa-heart footer-heart"></i> by{' '}
            <a
              href="https://web.virmatics.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="footer-virmatics-link"
            >
              Virmatics
            </a>
          </div>
        </footer>
      </div>
    </>
  );
}

function MainLayout() {
  return (
    <DepartementProvider>
      <MainLayoutInner />
    </DepartementProvider>
  );
}

export default MainLayout;
